import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import apiClient from "../../../services/apiClient";
import { Box, FormControl, FormHelperText, IconButton, InputAdornment, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export const InputField = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  onBlur,
  placeholder,
  isPassword,
  error,
  unit,
  disabled,
  required, // ✅ added
}: {
  label?: string;
  name: string;
  type?: "text" | "number" | "email" | "password" | "date" | "tel" | string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  isPassword?: boolean;
  error?: string;
  unit?: string;
  disabled?: boolean;
  required?: boolean; // ✅ added
}) => {
  const [show, setShow] = useState(false);

  const handleTogglePassword = () => setShow((prev) => !prev);

  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            mb: 0.5,
            color: "#333",
            display: "flex",
            alignItems: "center",
            gap: 0.3,
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: "red",
                fontSize: "1.2rem", // 🔹 increased size
                fontWeight: "bold", // 🔹 makes it more visible
                lineHeight: 1,
              }}
            >
              *
            </Typography>

          )}
        </Typography>
      )}

      <TextField
        fullWidth
        name={name}
        type={isPassword && !show ? "password" : type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        error={!!error}
        helperText={error || ""}
        size="small"
        variant="outlined"
        disabled={disabled}
        required={required} // ✅ native HTML required support
        InputProps={{
          endAdornment: (
            <>
              {isPassword ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePassword} edge="end">
                    {show ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : unit ? (
                <InputAdornment position="end">
                  <Typography sx={{ color: "#666", fontSize: "0.85rem" }}>
                    {unit}
                  </Typography>
                </InputAdornment>
              ) : undefined}
            </>
          ),
        }}
      />
    </Box>
  );
};
export const SelectField = ({
  label,
  name,
  value,
  onChange,
  options = [],
  error,
  disabled = false,
  placeholder,
  required = false, // ✅ new prop
}: {
  label?: React.ReactNode; // ✅ can be string or JSX
  name: string;
  value: string | number;
  onChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | SelectChangeEvent<string | number>
  ) => void;
  options?: (
    | string
    | { label: string; value: string | number; disabled?: boolean }
  )[];
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean; // ✅ new prop
}) => {
  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            mb: 0.5,
            color: error ? "error.main" : "text.primary",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {/* ✅ show red asterisk if required */}
          {label}
          {required && (
            <span
              style={{
                color: "red",
                fontSize: "1.2rem",
                fontWeight: "bold", 
                marginLeft: "3px",
                lineHeight: "1",
                verticalAlign: "middle", 
              }}
            >
              *
            </span>
          )}

        </Typography>
      )}

      <FormControl fullWidth size="small" error={!!error}>
        <Select
          name={name}
          value={value?.toString() || ""}
          onChange={onChange}
          displayEmpty
          disabled={disabled}
          sx={{
            backgroundColor: disabled ? "#f9f9f9" : "white",
            "& .MuiSelect-select": { py: 1.2, px: 1.5 },
          }}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
          )}

          {options.map((opt, i) => {
            const optionLabel =
              typeof opt === "string" ? opt : opt.label;
            const optionValue =
              typeof opt === "string" ? opt : opt.value;
            const optionDisabled =
              typeof opt === "string" ? false : opt.disabled ?? false;

            return (
              <MenuItem
                key={i}
                value={optionValue}
                disabled={optionDisabled}
              >
                {optionLabel}
              </MenuItem>
            );
          })}
        </Select>

        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    </Box>
  );
};

export const MultiSelectField = ({
  label,
  name,
  value,
  onChange,
  options = [],
  error,
}: {
  label?: string;
  name: string;
  value: string[];
  onChange: (name: string, values: string[]) => void;
  options?: { label: string; value: string }[];
  error?: string;
}) => {
  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            mb: 0.5,
            color: error ? "error.main" : "text.primary",
          }}
        >
          {label}
        </Typography>
      )}
      <FormControl fullWidth size="small" error={!!error}>
        <Select
          multiple
          name={name}
          value={value}
          onChange={(e) => onChange(name, e.target.value as string[])}
          renderValue={(selected) =>
            selected
              .map(
                (val) =>
                  options.find((opt) => opt.value === val)?.label || val
              )
              .join(", ")
          }
        >
          {options.map((opt, i) => (
            <MenuItem key={i} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    </Box>
  );
};


export default function AddOwnerModal({
  onClose,
  onSuccess,
  editData,
  mode = "add",
}: {
  onClose: () => void;
  onSuccess?: () => void;
  editData?: any;
  mode?: "add" | "edit";
}) {
  const fieldKeys = [
    "firstName",
    "lastName",
    "gender",
    "mobile",
    "email",
    "username",
    "password",
    "confirmPassword",
  ];

  const initialFormState = Object.fromEntries(fieldKeys.map((k) => [k, ""]));
  const [formData, setFormData] = useState<Record<string, string>>(
    initialFormState
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode !== "edit" || !editData?.user) return;

    // Convert snake_case to camelCase
    const toCamelCase = (str: string) =>
      str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

    const normalizeValue = (key: string, value: any) => {
      if (key === "gender") {
        const g = String(value || "").toLowerCase();
        if (["male", "female", "other"].includes(g)) {
          return g.charAt(0).toUpperCase() + g.slice(1);
        }
        return "";
      }
      return value ?? "";
    };

    setFormData((prev) => {
      const updated = { ...prev };

      Object.entries(editData.user).forEach(([apiKey, value]) => {
        const camelKey = toCamelCase(apiKey);
        if (camelKey in prev) {
          updated[camelKey] = normalizeValue(camelKey, value);
        }
      });

      return updated;
    });
  }, [editData, mode]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | SelectChangeEvent<string | number>
  ) => {
    const { name, value } = e.target as { name: string; value: string };
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const resetForm = (close = false) => {
    setFormData(initialFormState);
    setErrors({});
    if (close) onClose();
  };

  const validate = () => {
    const requiredFields = [
      { key: "firstName", message: "First name is required." },
      { key: "lastName", message: "Last name is required." },
      { key: "username", message: "Username is required." },
    ];

    const newErrors: Record<string, string> = Object.fromEntries(
      requiredFields
        .filter(({ key }) => !formData[key]?.trim())
        .map(({ key, message }) => [key, message])
    );

    if (!formData.gender) newErrors.gender = "Please select gender.";
    if (!/^\d{10}$/.test(formData.mobile))
      newErrors.mobile = "Enter valid 10-digit mobile number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Enter valid email address.";

    if (mode === "add") {
      if (!formData.password?.trim())
        newErrors.password = "Password is required.";
      if (formData.password !== formData.confirmPassword)
        newErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const isEdit = mode === "edit";
      const baseUser = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        username: formData.username,
        role: "parent",
        mobile: formData.mobile,
        is_2fa: 0,
        timezone_id: editData?.user?.timezone?.id || 1,
        ...(formData.gender && { gender: formData.gender }),
        ...(formData.profileImage && { profile_image: formData.profileImage }),
      };

      const payload = isEdit
        ? {
          id: editData.id,
          user_id: editData.user.id,
          user: baseUser,
        }
        : {
          user: {
            ...baseUser,
            password: formData.password,
            is_active: 1,
          },
        };

      const endpoint = isEdit
        ? `v1/resource/patients/${editData.id}`
        : "/v1/users/register";
      const method = isEdit ? "PATCH" : "POST";

      const response = await apiClient(endpoint, { method, body: payload });

      alert(isEdit ? "Pet owner updated successfully!" : "Pet owner added successfully!");
      resetForm(true);
      onSuccess?.();
    } catch (error: any) {
      console.error("❌ API error:", JSON.stringify(error, null, 2));
      alert(error.message || "Something went wrong. Please try again.");
    }
  };

  const fields = [
    { label: "First Name", name: "firstName" },
    { label: "Last Name", name: "lastName" },
    { label: "Gender", name: "gender", type: "select", options: ["Male", "Female", "Other"] },
    { label: "Mobile Number", name: "mobile", type: "tel", placeholder: "Enter mobile number" },
    { label: "Email", name: "email", type: "email", placeholder: "Enter email address" },
    { label: "Username", name: "username" },
    { label: "Password", name: "password", isPassword: true },
    { label: "Confirm Password", name: "confirmPassword", isPassword: true },
  ];

  const actions = [
    { label: "Cancel", onClick: () => resetForm(true), color: "red" },
    { label: mode === "edit" ? "Update" : "Save", onClick: handleSave, color: "green" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-[800px] max-w-[90vw] max-h-[90vh] overflow-y-auto shadow-lg">
        <h2 className="text-xl font-semibold mb-6 text-center border-b pb-2 text-gray-800">
          {mode === "edit" ? "Edit Pet Owner" : "Add Pet Owner"}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {fields
            .filter(
              (f) =>
                !(mode === "edit" && (f.name === "password" || f.name === "confirmPassword"))
            )
            .map((f) =>
              f.type === "select" ? (
                <SelectField
                  key={f.name}
                  {...f}
                  value={formData[f.name]}
                  onChange={handleChange}
                  error={errors[f.name]}
                />
              ) : (
                <InputField
                  key={f.name}
                  {...f}
                  value={formData[f.name]}
                  onChange={handleChange}
                  error={errors[f.name]}
                />
              )
            )}
        </div>

        <div className="border-t pt-4 mt-6 flex justify-center gap-3">
          {actions.map(({ label, onClick, color }) => (
            <button
              key={label}
              onClick={onClick}
              className={`px-5 py-2 bg-${color}-600 text-white rounded-md text-sm font-medium hover:bg-${color}-700 transition-all`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
